import {
  useRef, useState, useEffect, memo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { CircularProgress } from '@material-ui/core';

import { useAnalytics } from 'use-analytics';
import { getRateOptionRulesRequest, getRateOptionRulesSuccess } from '../../../store/slices/trip';
import {
  BookingAction, SearchStatus, TravelTypes, ScrollStates,
} from '../../../constants';
import withSpinner from '../../../HOCs/withSpinner';
import isElementInViewport from '../../../helpers/isElementInViewport';
import CruiseLineAdvisories from '../../CruiseLineAdvisories';
import getRateOptionRulesKey from '../../../helpers/getRateOptionRulesKey';
import { checkIsLoggedIn } from '../../../store/slices/user';
import RateRulesDescription from './RateRulesDescription';
import formatStateroomsForRequest from '../../../helpers/formatters/formatStateroomsForRequest';

const RateRules = ({
  itineraryId,
  rateOptionCode,
  tripConfirmation,
  selectedRoomIndex,
  cruiseLineId,
  resultno,
  sessionKey,
}) => {
  const staterooms = useSelector((state) => state.tripSearch.searchObj.staterooms);
  const isLoggedIn = useSelector(checkIsLoggedIn);
  const dispatch = useDispatch();
  const [doShowRateRules, setDoShowRateRules] = useState(false);
  const { pastPassengerNumber } = formatStateroomsForRequest(staterooms
    && staterooms[selectedRoomIndex], cruiseLineId);
  const key = getRateOptionRulesKey({
    itineraryId, rateOptionCode, roomIndex: selectedRoomIndex, pastPassengerNumber,
  });
  const allRateOptionRules = useSelector((store) => store.trip.rateOptionRules);

  const viewRulesEl = useRef(null);
  const scrollState = useSelector((store) => store.scroll);

  const {
    rateOptionRules, cruiseLineAdvisories, getRateRulesStatus,
  } = allRateOptionRules[key] || {};

  const getOptionRules = useCallback(() => {
    if (!rateOptionRules
      && getRateRulesStatus !== SearchStatus.DONE
      && getRateRulesStatus !== SearchStatus.SEARCHING) {
      dispatch(getRateOptionRulesRequest({
        itineraryId,
        rateOptionCode,
        key,
        selectedRoomIndex,
        cruiseLineId,
        resultno,
        sessionKey,
      }));
    }
  }, [rateOptionRules, getRateRulesStatus, dispatch, itineraryId,
    rateOptionCode, key, selectedRoomIndex, cruiseLineId, resultno, sessionKey]);

  useEffect(() => {
    if (scrollState.status === ScrollStates.DONE
      && isElementInViewport(viewRulesEl.current)) {
      getOptionRules();
    }
  }, [scrollState, getOptionRules]);

  const { track } = useAnalytics();

  useEffect(() => {
    if (tripConfirmation && tripConfirmation.trip) {
      const cruiseItinerary = tripConfirmation.trip.tripSegments
        .find((t) => t.mode === TravelTypes.cruise);
      // When user loggedIn in trip quote page, show latest rate rules.
      // otherwise show rate rules which is in trip
      const doSaveRateRulesFromTrip = !isLoggedIn
        || tripConfirmation.tripStatus === BookingAction.BOOK
        || tripConfirmation.tripStatus === BookingAction.CANCELLED;
      if (doSaveRateRulesFromTrip && cruiseItinerary && cruiseItinerary.itinerary
        && cruiseItinerary.itinerary.cabins && cruiseItinerary.itinerary.cabins.length) {
        cruiseItinerary.itinerary.cabins.forEach((cabin, roomIndex) => {
          dispatch(getRateOptionRulesSuccess({
            key: getRateOptionRulesKey({ cruiseItinerary: cruiseItinerary.itinerary, roomIndex }),
            rules: cruiseItinerary.itinerary.cabins[roomIndex].selectedRateOptionRules,
          }));
        });
      }
    }
  }, [tripConfirmation, dispatch, isLoggedIn]);

  const handleViewRatesClick = () => {
    setDoShowRateRules((v) => !v);
    track('rateRules.handleViewRatesClick', {
      doShowRateRules,
    });
  };

  useEffect(() => {
    if (doShowRateRules) {
      getOptionRules();
    }
  }, [doShowRateRules, rateOptionRules, dispatch,
    itineraryId, rateOptionCode, key, getRateRulesStatus, getOptionRules]);

  return (
    <>
      {
        getRateRulesStatus === SearchStatus.SEARCHING && !doShowRateRules
        && (
          <div className="mt-2 leading-none">
            <CircularProgress size={15} color="secondary" />
          </div>
        )
      }
      <a ref={viewRulesEl} className="text-theme cursor-pointer" role="button" tabIndex="-1" onClick={handleViewRatesClick} onKeyDown={handleViewRatesClick}>
        View rate rules
        {doShowRateRules && <FaChevronUp className="inline-block ml-1 text-sm" />}
        {!doShowRateRules && <FaChevronDown className="inline-block ml-1 text-sm" />}
      </a>

      {doShowRateRules && (
        <div>
          {withSpinner(() => (
            rateOptionRules && (
              <>
                <CruiseLineAdvisories advisories={cruiseLineAdvisories} className="p-2" />
                <RateRulesDescription rateOptionRules={rateOptionRules} />
              </>
            )
          ), getRateRulesStatus !== SearchStatus.DONE)}

          {getRateRulesStatus === SearchStatus.DONE
            && ((rateOptionRules || []).length === 0)
            && <div>Could not find any rate rules</div>}
        </div>
      )}
    </>
  );
};

RateRules.propTypes = {
  itineraryId: PropTypes.string.isRequired,
  rateOptionCode: PropTypes.string.isRequired,
  sessionKey: PropTypes.string.isRequired,
  resultno: PropTypes.string.isRequired,
  tripConfirmation: PropTypes.shape(),
  selectedRoomIndex: PropTypes.number,
  cruiseLineId: PropTypes.number.isRequired,
};

RateRules.defaultProps = {
  tripConfirmation: null,
  selectedRoomIndex: 0,
};

export default memo(RateRules);
